// /tutors/<slug> — профиль партнёра (ссылка из письма 3b и «Подробнее» на карте).
// С 28.10.2026 профиль должен убеждать родителя написать. Одна колонка на всех
// ширинах, по порядку: фото/инициалы + имя + предметы → цена и формат → WhatsApp /
// Zəng et → «Haqqımda» (длинный — «Davamı») → район и мини-карта (только building,
// круг ~500 м без точки) → «Yaxınlıqda başqa repetitorlar» → «Paylaş».
// Телефон: кнопки прилипают к низу, когда первый экран прокручен.
// Только данные анкеты: ни отзывов, ни рейтингов, ни «проверено».
// Анкета приходит из loader маршрута (сервер рисует профиль сразу), useQuery
// подхватывает её как initialData.
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { AboutBlock, SocialButtons } from "./AboutBlock";
import { ev } from "@/components/home/home-events";
import { CABINET_COPY, TUTORS_ME_PATH } from "./cabinet-copy";
import { MAP_COPY, TUTORS_PATH } from "./map-copy";
import { mapQueryHref } from "./map-href";
import { MapCanvas } from "./MapCanvas";
import { partnerQueryKey, type PartnerLoad } from "./partner-route";
import { ShareBlock } from "./ShareBlock";
import { PROFILE_COPY } from "./profile-copy";
import { ContactButtons, PartnerAvatar, PriceTag, SaveHeart, placeOf, subjectsOf } from "./TutorsMapPage";
import { MEET_LINE, showMeetLine } from "./meet-line";
import { formatKm } from "./my-location";
import { SiteFooter, SiteHeader, type SiteLang } from "@/components/site/SiteChrome";
import { BAKU_CITY, FORMATS, GRADES, METRO, labelOf } from "@/data/map-taxonomy";
import { alsoTravelsLine } from "@/lib/map-building-travel";
import { isMyPartner } from "@/lib/contact-self";
import type { Nearby } from "@/lib/tutor-nearby";
import { profileCrumbSubject, profileSubjectPages, subjectLabel, subjectPath } from "@/lib/tutor-subject-pages";
import { instagramHref } from "@/lib/instagram";
import { cityOfPartner } from "@/lib/map-cities";
import { getMapsBrowserKey } from "@/lib/map-partners.functions";
import { getPublicPartner, type PublicPartner } from "@/lib/map-public.functions";

const PHOTO_PX = 128;
/** Радиус круга на мини-карте, м: район виден, дом — нет. */
const AREA_M = 500;


/** Кнопки страницы «Анкета не найдена». */
const NOT_FOUND_ACTIONS: Record<SiteLang, { map: string; cabinet: string }> = {
  ru: { map: "К карте", cabinet: "Я репетитор — мой кабинет" },
  az: { map: "Xəritəyə", cabinet: "Mən repetitoram — kabinetim" },
  en: { map: "To the map", cabinet: "I'm a tutor — my dashboard" },
};

export function PartnerPage({
  lang,
  slug,
  initial,
  nearby,
  pageCodes = [],
}: {
  lang: SiteLang;
  slug: string;
  initial?: PartnerLoad | null;
  nearby?: Nearby | null;
  /** Страницы предметов, которые есть: крошки и ссылки ведут только на них (29.10.2026). */
  pageCodes?: string[];
}) {
  const c = MAP_COPY[lang];
  const fetchOne = useServerFn(getPublicPartner);
  const q = useQuery({
    queryKey: partnerQueryKey(slug),
    queryFn: () => fetchOne({ data: { slug } }),
    initialData: initial ?? undefined,
    staleTime: 60_000,
  });
  const langHref = {
    ru: `${TUTORS_PATH.ru}/${slug}`,
    az: `${TUTORS_PATH.az}/${slug}`,
    en: `${TUTORS_PATH.en}/${slug}`,
  };
  const p = q.data?.row ?? null;
  const cta = useRef<HTMLDivElement>(null);
  const prof = PROFILE_COPY[lang];
  useProfileView(p);
  return (
    <div className="hp" lang={lang}>
      <SiteHeader lang={lang} langHref={langHref} />
      <main className="tj-wrap tm-partner">
        {p ? (
          <ProfileCrumbs p={p} lang={lang} subject={profileCrumbSubject(p, pageCodes)} />
        ) : (
          <a className="tm-link" href={TUTORS_PATH[lang]}>
            {c.backToMap}
          </a>
        )}
        {q.isError && <p className="tm-err">{c.loadError}</p>}
        {q.isSuccess && !p && (
          <div className="tm-notfound">
            <p>{c.notFound}</p>
            {/* Не нашли анкету: на карту или — репетитору — в свой кабинет (24.10.2026). */}
            <div className="tj-row">
              <a className="hp-btn" href={TUTORS_PATH[lang]}>
                {NOT_FOUND_ACTIONS[lang].map}
              </a>
              <a className="hp-btn hp-btn--outline" href={TUTORS_ME_PATH[lang]} rel="nofollow">
                {NOT_FOUND_ACTIONS[lang].cabinet}
              </a>
            </div>
          </div>
        )}
        {p && (
          <div className="tm-profile">
            {/* Пауза из кабинета: в списке и на карте анкеты нет, по ссылке — с пометкой. */}
            {p.is_paused && <p className="tm-paused" role="note">{p.paused_until ? CABINET_COPY[lang].profilePausedUntil(p.paused_until.split("-").reverse().join(".")) : CABINET_COPY[lang].profilePaused}</p>}
            <ProfileHead p={p} lang={lang} ctaRef={cta} />
            <PhotoHint p={p} lang={lang} />
            {p.about && <AboutBlock about={p.about} lang={lang} labels={{ title: prof.about[p.kind], more: prof.more, less: prof.less }} />}
            <ProfileInstagram p={p} lang={lang} />
            <MiniMap p={p} lang={lang} />
            <SubjectLinks p={p} lang={lang} codes={profileSubjectPages(p, pageCodes)} />
            <NearbyBlock me={p} nearby={nearby ?? null} lang={lang} />
            <div className="tm-share-row">
              <ShareBlock slug={p.slug} name={p.name} lang={lang} />
            </div>
            {/* «Это ваш профиль?» — в кабинет: вход по ссылке на почту анкеты. */}
            <div className="tm-edit">
              <a className="tj-plain tm-edit-link" href={TUTORS_ME_PATH[lang]} rel="nofollow">
                {CABINET_COPY[lang].profileLink}
              </a>
            </div>
            <StickyContact p={p} lang={lang} target={cta} />
          </div>
        )}
        {/* «Отчёты не зависят от карты» — у анкеты; на «не найдена» не к месту. */}
        {p && <p className="tm-muted tm-note">{c.separate}</p>}
      </main>
      <SiteFooter lang={lang} langHref={langHref} />
    </div>
  );
}

/** tutor_profile_view (subject, district) — один раз на анкету. Без имени и контактов. */
function useProfileView(p: PublicPartner | null) {
  const sent = useRef<string | null>(null);
  useEffect(() => {
    if (!p || sent.current === p.id) return;
    sent.current = p.id;
    ev("tutor_profile_view", { subject: p.subjects[0] ?? "", district: p.district ?? (p.address_mode === "online" ? "online" : (p.travel_districts[0] ?? p.city ?? "")) });
  }, [p]);
}

/** Первый экран: фото/инициалы, имя, предметы → цена, формат, где → WhatsApp / Zəng et. */
function ProfileHead({ p, lang, ctaRef }: { p: PublicPartner; lang: SiteLang; ctaRef: React.RefObject<HTMLDivElement | null> }) {
  const c = MAP_COPY[lang];
  const extra =
    p.kind === "tutor"
      ? p.grades.map((g) => labelOf(GRADES, g, lang)).join(", ")
      : p.age_min || p.age_max
        ? `${[p.age_min, p.age_max].filter(Boolean).join("–")} ${c.years}`
        : "";
  const travels = alsoTravelsLine(p, lang);
  return (
    <article className="tm-card tm-card--profile tm-phead">
      <div className="tm-phead__who">
        <ProfilePhoto p={p} lang={lang} />
        <div className="tm-phead__id">
          {/* Имя — единственный H1 страницы. */}
          <h1 className="tm-card__name">{p.name}</h1>
          {p.subjects.length > 0 && <p className="tm-card__subj">{subjectsOf(p, lang)}</p>}
        </div>
      </div>
      <div className="tm-phead__offer">
        {p.price_azn !== null && <PriceTag p={p} lang={lang} />}
        {p.trial_free && <span className="tm-trial">{c.trialFree}</span>}
        {showMeetLine(p) && <span className="tm-meet">{MEET_LINE[lang]}</span>}
        <span className="tm-muted">{[p.formats.map((f) => labelOf(FORMATS, f, lang)).join(", "), extra].filter(Boolean).join(" · ")}</span>
        <span className="tm-muted">{placeOf(p, lang)}</span>
        {travels && (
          <span className="tm-muted">
            {c.alsoTravelsTo}: {travels}
          </span>
        )}
      </div>
      <div ref={ctaRef} className="tm-phead__cta">
        <ContactButtons id={p.id} lang={lang} source="profile" showPhone={p.show_phone !== false} />
        <SaveHeart id={p.id} lang={lang} />
      </div>
    </article>
  );
}

/**
 * Телефон: когда кнопки первого экрана ушли вверх — те же кнопки внизу экрана.
 * На компьютере блок скрыт стилями (tutors-map.css).
 */
function StickyContact({ p, lang, target }: { p: PublicPartner; lang: SiteLang; target: React.RefObject<HTMLDivElement | null> }) {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const node = target.current;
    if (!node) return;
    const io = new IntersectionObserver(([e]) => setOn(!e.isIntersecting && e.boundingClientRect.top < 0));
    io.observe(node);
    return () => io.disconnect();
  }, [target]);
  if (!on) return null;
  return (
    <div className="tm-sticky" data-sticky-contact="">
      <ContactButtons id={p.id} lang={lang} source="profile" showPhone={p.show_phone !== false} />
    </div>
  );
}

/**
 * Нет фото — плашка «Şəkil əlavə edin» только владельцу: с этого браузера входили
 * в кабинет этой анкеты (localStorage, src/lib/contact-self.ts). Посетитель её не
 * видит, сервер её не рисует (только после гидратации).
 */
function PhotoHint({ p, lang }: { p: PublicPartner; lang: SiteLang }) {
  const [mine, setMine] = useState(false);
  useEffect(() => setMine(isMyPartner(p.id)), [p.id]);
  if (p.photo_url || !mine) return null;
  const t = PROFILE_COPY[lang];
  return (
    <p className="tm-photohint" role="note" data-photo-hint="">
      {t.photoHint}{" "}
      <a href={TUTORS_ME_PATH[lang]} rel="nofollow">
        → {t.photoHintLink}
      </a>
    </p>
  );
}

const HOME_HREF: Record<SiteLang, string> = { az: "/az", ru: "/ru", en: "/en" };

/** Крошки «Yolutap › Repetitorlar › Предмет › Имя» (29.10.2026); BreadcrumbList — в head. */
function ProfileCrumbs({ p, lang, subject }: { p: PublicPartner; lang: SiteLang; subject: string | null }) {
  return (
    <nav className="tm-crumbs tm-crumbs--profile" aria-label="breadcrumb">
      <a href={HOME_HREF[lang]}>Yolutap</a>
      <span aria-hidden="true"> › </span>
      <a href={TUTORS_PATH[lang]}>{MAP_COPY[lang].breadcrumbHub}</a>
      {subject && (
        <>
          <span aria-hidden="true"> › </span>
          <a href={subjectPath(subject, lang)}>{subjectLabel(subject, lang)}</a>
        </>
      )}
      <span aria-hidden="true"> › </span>
      <span aria-current="page">{p.name}</span>
    </nav>
  );
}

/** Ссылки на страницы предметов анкеты (только те, что есть). */
function SubjectLinks({ p, lang, codes }: { p: PublicPartner; lang: SiteLang; codes: string[] }) {
  if (!codes.length) return null;
  return (
    <nav className="tm-subjlinks" aria-label={PROFILE_COPY[lang].subjectPages}>
      <span className="tm-muted">{PROFILE_COPY[lang].subjectPages}:</span>{" "}
      {codes.map((s, i) => (
        <span key={s}>
          {i > 0 && " · "}
          <a href={subjectPath(s, lang)}>{subjectLabel(s, lang)}</a>
        </span>
      ))}
    </nav>
  );
}

/** «Yaxınlıqda başqa repetitorlar»: 3–4 карточки-ссылки (src/lib/tutor-nearby.ts). */
function NearbyBlock({ me, nearby, lang }: { me: PublicPartner; nearby: Nearby | null; lang: SiteLang }) {
  if (!nearby || !nearby.rows.length) return null;
  const t = PROFILE_COPY[lang];
  return (
    <section className="tm-nearby" aria-labelledby="tm-nearby-h" data-nearby={nearby.mode}>
      <h2 id="tm-nearby-h" className="tm-aboutbox__h">
        {nearby.mode === "near" ? t.nearby[me.kind] : t.sameSubject[me.kind]}
      </h2>
      <ul className="tm-nearby__list">
        {nearby.rows.map(({ p, km }) => (
          <li key={p.id}>
            <a
              className="tm-nearby__card"
              href={`${TUTORS_PATH[lang]}/${p.slug}`}
              onClick={() => ev("tutor_nearby_click", { subject: me.subjects[0] ?? "", tutor_id: p.id, mode: nearby.mode })}
            >
              <PartnerAvatar p={p} size={48} />
              <span className="tm-nearby__body">
                <b>{p.name}</b>
                <span className="tm-nearby__subj">{subjectsOf(p, lang)}</span>
                {p.price_azn !== null && <PriceTag p={p} lang={lang} />}
                <span className="tm-muted">{km !== null ? `${formatKm(km, lang)} · ${placeOf(p, lang)}` : placeOf(p, lang)}</span>
              </span>
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** «Instagram» → instagram.com/<ник>; только если ник есть и он чистый (src/lib/instagram.ts). */
function ProfileInstagram({ p, lang }: { p: PublicPartner; lang: SiteLang }) {
  const href = instagramHref(p.instagram);
  if (!href) return null;
  return <SocialButtons links={[{ service: "instagram", href }]} label="Instagram" lang={lang} />;
}

/** Крупное фото; нажатие открывает его целиком. Без фото — инициалы, как на карте. */
function ProfilePhoto({ p, lang }: { p: PublicPartner; lang: SiteLang }) {
  const c = MAP_COPY[lang];
  const [open, setOpen] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const closer = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const back = opener.current;
    closer.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      back?.focus();
    };
  }, [open]);
  if (!p.photo_url) return <PartnerAvatar p={p} size={PHOTO_PX} />;
  return (
    <>
      <button
        ref={opener}
        type="button"
        className="tm-photo-btn"
        aria-label={c.photoOpen}
        onClick={() => setOpen(true)}
      >
        <PartnerAvatar p={p} size={PHOTO_PX} />
      </button>
      {open && (
        <div
          className="tm-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={p.name}
          onClick={() => setOpen(false)}
        >
          <img
            src={p.photo_url}
            alt={p.name}
            className="tm-lightbox__img"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            ref={closer}
            type="button"
            className="tm-lightbox__close"
            aria-label={c.photoClose}
            onClick={() => setOpen(false)}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
}

/**
 * Где занимается партнёр: круг ~500 м вокруг точки анкеты (с 28.10.2026 —
 * без метки и без адреса: район виден, дом — нет). Точки нет, но есть метро —
 * карта с центром у метро, без пина, с подписью района. Выезд, онлайн или
 * ни точки, ни метро — мини-карты нет. Скрипт карт и ключ — только когда
 * блок подъехал к экрану (бесплатный лимит загрузок).
 */
function MiniMap({ p, lang }: { p: PublicPartner; lang: SiteLang }) {
  const c = MAP_COPY[lang];
  const fetchKey = useServerFn(getMapsBrowserKey);
  const box = useRef<HTMLDivElement>(null);
  const [near, setNear] = useState(false);
  const [mapsKey, setMapsKey] = useState<string | null | undefined>(undefined);
  const city = cityOfPartner(p);
  const metro = p.metro && city === BAKU_CITY ? METRO.find((m) => m.code === p.metro) : undefined;
  const pin = p.lat !== null && p.lng !== null ? { lat: p.lat, lng: p.lng } : null;
  const center = pin ?? (metro ? { lat: metro.lat, lng: metro.lng } : null);
  const show = p.address_mode === "building" && center !== null;

  useEffect(() => {
    const node = box.current;
    if (!show || !node || near) return;
    const io = new IntersectionObserver((es) => es.some((e) => e.isIntersecting) && setNear(true), {
      rootMargin: "200px",
    });
    io.observe(node);
    return () => io.disconnect();
  }, [show, near]);
  useEffect(() => {
    if (!near) return;
    fetchKey()
      .then((r) => setMapsKey(r.key))
      .catch(() => setMapsKey(null));
  }, [near, fetchKey]);

  if (!show || !center) return null;
  return (
    <section className="tm-profile__map" aria-label={placeOf(p, lang)}>
      <div ref={box} className="tm-minimap">
        {mapsKey !== undefined && (
          <MapCanvas
            mapsKey={mapsKey}
            lang={lang}
            points={[]}
            activeId={null}
            onSelect={() => {}}
            center={center}
            zoom={14}
            area={pin ? { ...pin, radius: AREA_M } : null}
            labels={{ loading: "…", error: c.loadError }}
          />
        )}
      </div>
      <div className="tm-minimap__bar">
        <span className="tm-muted">{placeOf(p, lang)}</span>
        {/* Ориентир — со слов самого репетитора, как в анкете. */}
        {p.landmark && (
          <span className="tm-muted" data-landmark="">
            {c.landmark}: {p.landmark}
          </span>
        )}
        <a
          className="tm-link"
          href={mapQueryHref(TUTORS_PATH[lang], {
            layer: p.kind,
            district: p.district ?? undefined,
            city: city ?? undefined,
          })}
        >
          {c.openOnMap}
        </a>
      </div>
    </section>
  );
}
